import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Client } from "@interfaces/models/client.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { ClientService } from "@services/client.service";
import { ExportLogService } from "@services/exportLog.service";
import { ProjectService } from "@services/project.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { QuotationAnalysisFormService } from "@services/quotationAnalysisForm.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "quotationAnalysisForms",
	styleUrls: ["quotationAnalysisForms.css"],
	templateUrl: "quotationAnalysisForms.html"
})
export class QuotationAnalysisFormsAdminComponent {
	canExportGrid = false;
	clients: Client[] = [];
	deleteEnabled = true;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	itemType = "QAF"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: any[] = [];
	projects: any[] = [];
	title: string;
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private clientService: ClientService, private exportLogService: ExportLogService, private protectedGridService: ProtectedGridService, private itemService: QuotationAnalysisFormService, private projectService: ProjectService, private userProfileService: UserProfileService) {
		this.headerPrimaryButtons.push({ icon: "fal fa-plus", method: "addNewItem", text: `New ${this.itemType}` });
		this.title = `${this.itemType}s`;
		this.viewRow = this.viewRow.bind(this);
		this.editRow = this.editRow.bind(this);
		this.deleteRow = this.deleteRow.bind(this);
		this.duplicateRow = this.duplicateRow.bind(this);
		this.toolingFixtureCost = this.toolingFixtureCost.bind(this);
		this.getClients();
		this.getProjects();
		this.getUserProfiles();
		this.getItems();
		this.checkGridExportPermissions();
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/create`]);
	}

	boolYesNo(cellInfo: any) {
		if (cellInfo.value === true) {
			return "YES";
		}
		else {
			return "NO";
		}
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("QAFs")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	deleteRow(e: any) {
		const result = confirm("Are you sure you want to delete this item?");
		if(result)
		{
			this.itemService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success(this.itemType + " deleted!");
						this.getItems();
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}

	duplicateRow(e: any) {
		const id = e.row.data.id;
		if (!id) { return; }
		if (confirm("Duplicate this QAF?") === false) { return; }
		this.gridLoading = true;
		// Fetch the full QAF so the clone carries all line data, then create a fresh copy.
		this.itemService.getSingleById(id)
			.subscribe(
				(res: any) => {
					const copy: any = JSON.parse(JSON.stringify(res.response));
					copy.id = 0;
					delete copy.lookupId;
					copy.approved = false;
					copy.approvedById = null;
					delete copy.approvedOn; // non-nullable DateTime on the API — send absent, not null, on create
					delete copy.childCount; // transient grid-only field
					delete copy.createdById; // the API stamps whoever duplicates it
					copy.partNumberExists = false;
					this.itemService.createSingle(copy)
						.subscribe(
							() => { this.toastService.success(this.itemType + " duplicated"); this.getItems(); },
							(err) => { console.log(err); this.gridLoading = false; this.toastService.error(err?.error?.error || "Could not duplicate QAF"); }
						);
				},
				(err) => { console.log(err); this.gridLoading = false; this.toastService.error("Could not load QAF to duplicate"); }
			);
	}

	editRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}_edit`]);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => this.clients = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getItems() {
		this.gridLoading = true;
		this.itemService.getAll()
			.subscribe(
				(res: any) => {
					const all = res.response.items;
					// Count children per parent, then show only top-level QAFs (those with no parent).
					const childCounts: any = {};
					all.forEach((x: any) => {
						if (x.parentQuotationAnalysisFormId) {
							childCounts[x.parentQuotationAnalysisFormId] = (childCounts[x.parentQuotationAnalysisFormId] || 0) + 1;
						}
					});
					this.items = all.filter((x: any) => !x.parentQuotationAnalysisFormId);
					this.items.forEach((x: any) => { x.childCount = childCounts[x.id] || 0; });
					this.gridLoading = false;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getProjects() {
		this.projectService.getAll()
			.subscribe(
				(res: any) => this.projects = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	// Resolves the createdById stamped on the QAF to a name for the grid column.
	getUserProfiles() {
		this.userProfileService.getAll()
			.subscribe(
				(res: any) => this.userProfiles = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "QAFs";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}

	toolingFixtureCost(rowData: any): number {
		return (rowData.totalToolingCosts || 0) + (rowData.totalFixtureCosts || 0);
	}

	viewRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}`]);
	}
}